import { useCallback, useEffect, useRef, useState } from "react";
import { ReceivableQuotesRes, XflowReceivableRes } from "../../types/XFlow";
import Modal from "../Modal";
import { useXFlowPartnerContext } from "../../context/XFlowPartners";
import { useSearchParams } from "react-router-dom";
import {
  xflow_receivables_get_quotes,
  xflow_reconcile_receivables,
} from "../../constants/apis";
import { useAppContext } from "../../context/AppContext";
import { debounce, getTomorrow } from "../../utils";
import Tooltip from "../Tooltip";
import { toast } from "react-toastify";
import { SpinnerIcon } from "../Icons";
import {
  useXFlowFeePlansContext,
  XFlowFeePlansContextProvider,
} from "../../context/XFlowFeePlans";
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";

type PayoutFormType = {
  payout_amount: string;
};

export default function ReconcileModalBtn({
  receivablesData,
  refetchReceivables,
}: {
  receivablesData: XflowReceivableRes[] | null;
  refetchReceivables: () => void;
}) {
  const reconcileModalRef = useRef<HTMLDialogElement>(null);

  const toggleReconcileModal = () => {
    if (reconcileModalRef.current?.open) {
      reconcileModalRef.current?.close();
    } else {
      reconcileModalRef.current?.showModal();
    }
  };

  const activatedReceivableData = receivablesData?.filter(
    (el) => el.status === "activated"
  );

  return (
    <XFlowFeePlansContextProvider>
      <div>
        <button
          type="button"
          className="text-custom-blue-medium border border-custom-blue-medium px-2 py-[2px] rounded-md"
          onClick={toggleReconcileModal}
        >
          Reconcile
        </button>

        <Modal
          ref={reconcileModalRef}
          toggleDialog={toggleReconcileModal}
          className="w-[500px] p-6 rounded-md backdrop:bg-black/50"
        >
          <h2 className="font-medium text-xl mb-5">Reconcile Amount</h2>
          <div>
            {activatedReceivableData && activatedReceivableData.length > 0 ? (
              <ReconcileForm
                activatedReceivableData={activatedReceivableData}
                refetchReceivables={refetchReceivables}
                toggleReconcileModal={toggleReconcileModal}
              />
            ) : (
              <p>No data found</p>
            )}
          </div>
        </Modal>
      </div>
    </XFlowFeePlansContextProvider>
  );
}

function ReconcileForm({
  activatedReceivableData,
  refetchReceivables,
  toggleReconcileModal,
}: {
  activatedReceivableData: XflowReceivableRes[];
  refetchReceivables: () => void;
  toggleReconcileModal: () => void;
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedReceivable, setSelectedReceivable] =
    useState<XflowReceivableRes>();

  // const { data: allPartners } = useXFlowPartnerContext();
  const { jwtToken } = useAppContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const partner_id = searchParams.get("partner_id");
  const receivableId = searchParams.get("receivable_id");

  // const partner = allPartners?.find((partner) => partner.id === partner_id);

  useEffect(() => {
    const id = activatedReceivableData[0].id;

    setSearchParams({
      partner_id: partner_id ? partner_id : "",
      receivable_id: id,
    });
    setSelectedReceivable(activatedReceivableData.find((el) => el.id === id));
  }, []);

  const nextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };
  const prevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParams({
      partner_id: partner_id!,
      receivable_id: e.currentTarget.value,
    });

    setSelectedReceivable(
      activatedReceivableData.find((el) => el.id === e.currentTarget.value)
    );
  };

  const methods = useForm<PayoutFormType>({
    mode: "onChange",
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmitHandler: SubmitHandler<PayoutFormType> = async (values) => {
    // console.log(values);

    try {
      const res = await fetch(xflow_reconcile_receivables, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify({
          receivables_id: receivableId,
          amount: values.payout_amount,
        }),
      });
      const data = await res.json();

      if (data.status === "success") {
        toast.success(
          data?.message || data?.msg || "Amount reconcile is complete"
        );
        refetchReceivables();
      } else {
        toast.error("something went wrong!");
      }
    } catch (error) {
      console.log(error);
      toast.error("something went wrong!");
    } finally {
      toggleReconcileModal();
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className="text-[#242424]"
        onSubmit={handleSubmit(onSubmitHandler)}
        noValidate
      >
        {currentStep === 2 && (
          <button
            type="button"
            disabled={isSubmitting}
            className="w-[87px] h-[44px] border border-[#D0D5DD] text-[#344054] font-medium rounded-lg"
            onClick={prevStep}
          >
            Back
          </button>
        )}

        {currentStep === 1 && (
          <div className="space-y-2">
            {activatedReceivableData.map((el) => (
              <label
                key={el.id}
                className="flex items-center gap-3 pb-1 cursor-pointer border-b"
              >
                <input
                  type="radio"
                  name="receivable_id"
                  className="size-5 cursor-pointer"
                  value={el.id}
                  onChange={handleOnChange}
                  checked={receivableId === el.id}
                />
                <div className="w-full flex items-start justify-between">
                  <div>
                    {/* <h3 className="text-lg">{partner?.nickname}</h3> */}
                    <p className="text-sm">{el.invoice.reference_number}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(el.created * 1000).toDateString()}
                    </p>
                  </div>

                  <p className="text-lg">
                    {el.amount_reconcilable
                      ? `${el.currency} ${el.amount_reconcilable}`
                      : "nil"}
                  </p>
                </div>
              </label>
            ))}
          </div>
        )}

        {currentStep === 2 && (
          <ReceivablePayout selectedReceivable={selectedReceivable} />
        )}

        {currentStep === 1 && (
          <div className="w-full pt-4 flex justify-end">
            <button
              type="button"
              className="float-end w-[146px] h-[44px] rounded-md bg-custom-blue-medium text-white font-medium"
              onClick={nextStep}
            >
              Next
            </button>
          </div>
        )}
      </form>
    </FormProvider>
  );
}

function ReceivablePayout({
  selectedReceivable,
}: {
  selectedReceivable: XflowReceivableRes | undefined;
}) {
  const { jwtToken, user } = useAppContext();
  const { data: xflowAllFeePlans } = useXFlowFeePlansContext();
  const {
    register,
    formState: { errors, isSubmitting },
    setValue,
    trigger,
  } = useFormContext<PayoutFormType>();

  const [receivableQuote, setReceivableQuote] = useState<{
    quote: ReceivableQuotesRes | null;
    loading: boolean;
    error: string;
  }>({
    loading: false,
    quote: null,
    error: "",
  });
  const [grossAmount, setGrossAmount] = useState("");
  const [payoutFee, setPayoutFee] = useState(0);

  useEffect(() => {
    if (selectedReceivable?.amount_reconcilable) {
      const defaultVal = selectedReceivable.amount_reconcilable;
      setValue("payout_amount", defaultVal);
    }
  }, [selectedReceivable, setValue]);

  const payoutFeePlan = xflowAllFeePlans
    ?.at(0)
    ?.payout.find(
      (el) => el.source_currency === "USD" && el.destination_currency === "INR"
    );

  const getQuotes = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValid = await trigger("payout_amount");

    if (!isValid) return;

    const { value } = e.target;

    const numericGross = parseFloat(value);

    const feeRate = parseFloat(payoutFeePlan?.variable ?? "0");
    const fee = (numericGross * feeRate) / 100;
    const netA = (numericGross - fee).toFixed(2);

    setPayoutFee(fee);
    setGrossAmount(value);

    const payload = {
      sell_amount: netA,
      sell_currency: "USD",
      buy_currency: "INR",
      type: "payout_fx",
    };

    setReceivableQuote((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const res = await fetch(xflow_receivables_get_quotes, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          Content_Type: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setReceivableQuote((prev) => ({ ...prev, quote: data }));
    } catch (error) {
      // console.log(error);
      setReceivableQuote({
        quote: null,
        loading: false,
        error: "Something went wrong, please enter the amount again",
      });
    } finally {
      setReceivableQuote((prev) => ({ ...prev, loading: false }));
    }
  };

  const debouncedGetQuotes = useCallback(debounce(getQuotes, 500), []);

  return (
    <div className="space-y-5 py-4 text-sm">
      <div>
        <p className="flex justify-between">
          <span>Invoice Number</span>
          <span className="text-gray-500">
            {selectedReceivable?.invoice.reference_number}
          </span>
        </p>
        <p className="flex justify-between">
          <span>Amount Pending</span>
          <span className="text-gray-500">
            {selectedReceivable?.amount_reconcilable
              ? `${selectedReceivable?.currency} ${selectedReceivable?.amount_reconcilable}`
              : "nil"}
          </span>
        </p>
      </div>

      <div>
        <div className="flex items-end">
          <label className="w-full">
            <span className="block text-[#344054] text-sm font-medium">
              Payout Amount *
            </span>
            <input
              type="number"
              inputMode="decimal"
              className="w-full p-[10px] border-[0.66px] border-[#d0d5dd] border-r-0 rounded-l-[8px] outline-0"
              onFocus={debouncedGetQuotes}
              // defaultValue={selectedReceivable?.amount_reconcilable}
              autoFocus
              {...register("payout_amount", {
                required: "Amount is required",
                validate: (value) => {
                  const numeric = parseFloat(value);
                  if (isNaN(numeric)) return "Invalid number";
                  if (numeric < 1) return "Amount must be at least 1";

                  const maxRaw = selectedReceivable?.amount_reconcilable ?? "0";
                  const max = parseFloat(maxRaw);
                  if (numeric > max) return `Amount cannot exceed ${max}`;

                  return true;
                },
                onChange: (e) => debouncedGetQuotes(e),
              })}
            />
          </label>
          <select className="p-[10px] border-[0.66px] border-[#d0d5dd] rounded-r-[8px] outline-0">
            <option value="USD">USD</option>
          </select>
        </div>
        {errors.payout_amount && (
          <p className="text-red-500 text-xs mt-1">
            {errors.payout_amount.message}
          </p>
        )}
      </div>

      {receivableQuote.loading ? (
        <div className="flex animate-pulse space-x-4">
          <div className="flex-1 space-y-6 py-1">
            <div className="h-2 rounded bg-gray-200"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 h-2 rounded bg-gray-200"></div>
                <div className="col-span-1 h-2 rounded bg-gray-200"></div>
              </div>
              <div className="h-2 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {receivableQuote.quote && (
            <div className="space-y-4 text-gray-500">
              <div className="pb-2 border-b">
                <p className="flex justify-between">
                  <span>Gross Amount</span>
                  <span className="flex items-center gap-2">
                    {`${selectedReceivable?.currency} ${grossAmount}`}
                  </span>
                </p>
                <p className="flex justify-between">
                  <span>Payout Fee</span>
                  <span className="flex items-center gap-2 text-red-400">
                    {`- ${
                      receivableQuote.quote.data.sell.currency
                    } ${payoutFee.toFixed(2)}`}
                  </span>
                </p>
              </div>

              <div className="space-y-1 pb-2 border-b">
                <p className="flex justify-between">
                  <span>
                    Net Amount {`(${receivableQuote.quote.data.sell.currency})`}
                  </span>
                  <span className="flex items-center gap-2">
                    {`${receivableQuote.quote.data.sell.currency} ${receivableQuote.quote.data.sell.amount}`}
                  </span>
                </p>

                <div className="flex justify-between items-center">
                  <p>{`FX (${receivableQuote.quote.data.dealt_currency} 1)`}</p>

                  <div className="text-gray-500 flex items-center gap-2 border-b border-dashed">
                    <p>INR {receivableQuote.quote.data.rate.user}</p>

                    {/* <Tooltip
                      content={
                        <div className="text-[10px]">
                          <p className="flex justify-between gap-2">
                            <span>Transaction Rate</span>
                            <span>{receivableQuote.quote.data.rate.user}</span>
                          </p>
                          <p className="flex justify-between pb-2 border-b">
                            <span>Mid Market Rate</span>
                            <span>
                              {receivableQuote.quote.data.rate.mid_market}
                            </span>
                          </p>

                          <p className="mt-1 flex justify-between">
                            <span>Last Updated</span>
                            <span>
                              {new Date(
                                receivableQuote.quote.data.rate.valid_from *
                                  1000
                              ).toDateString()}
                            </span>
                          </p>
                          <p className="flex justify-between">
                            <span>Valid Till</span>
                            <span>
                              {new Date(
                                receivableQuote.quote.data.rate.valid_to * 1000
                              ).toDateString()}
                            </span>
                          </p>
                        </div>
                      }
                    /> */}
                  </div>
                </div>
                <p className="text-lg flex justify-between">
                  <span className="text-[#242424]">Payout Amount:</span>
                  <span className="text-custom-blue-medium">
                    {`${receivableQuote.quote.data.buy.currency} ${receivableQuote.quote.data.buy.amount}`}
                  </span>
                </p>
              </div>

              <div>
                <h3 className="text-[#242424] font-medium mb-1">
                  Payout Summary
                </h3>
                <p className="flex justify-between">
                  <span>Bank Account</span>
                  <span className="flex items-center gap-2">
                    Ending with{" "}
                    {`${
                      user?.bank_details?.at(0).xflow_response?.bank_account
                        ?.last4
                    }`}
                  </span>
                </p>
                <p className="flex justify-between">
                  <span>Payout Date</span>
                  <span className="flex items-center gap-2">
                    {getTomorrow()}, by 9 PM IST
                  </span>
                </p>
              </div>
            </div>
          )}

          {receivableQuote.error && (
            <p className="text-red-400 mt-1">{receivableQuote.error}</p>
          )}

          {receivableQuote.quote && (
            <div className="flex justify-end mt-5">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-[146px] h-[44px] flex items-center justify-center rounded-md bg-custom-blue-medium text-white font-medium disabled:bg-custom-blue-medium/70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <SpinnerIcon className="size-4 text-gray-200 animate-spin fill-custom-blue-medium" />
                ) : (
                  "Reconcile"
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}